import InternalHeader from '../InternalHeader/InternalHeader';
import Button from '../UI/Button/Button';
import Dropdown from '../UI/Dropdown/Dropdown';
import styles from './ExcursionSignUpModal.module.css';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Icon from '../Icon';
import ScalePreview from '../UI/ScalePreview/ScalePreview';
import { addDays, format, parse } from 'date-fns';
import { ru } from 'date-fns/locale';
import cn from 'classnames';
import { sendLeadToWhatsApp } from '../../utils/whatsapp';

const YANDEX_MAP_API_URL = "https://api-maps.yandex.ru/2.1/?lang=ru_RU";

interface ExcursionSignUpModalProps {
  isActive: boolean;
  onClose: () => void;
}

export default function ExcursionSignUpModal({ isActive, onClose }: ExcursionSignUpModalProps) {
  const [isSent, setIsSent] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [zoomLevel, setZoomLevel] = useState(160);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [currentHour, setCurrentHour] = useState(new Date().getHours());
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<any>(null);
  const centerCoordinates = useMemo(() => [42.887141, 47.624338], []);

  const getNextThreeDates = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const startDayOffset = (currentHour > 16 || (currentHour === 16 && currentMinutes >= 30)) ? 1 : 0;

    return Array.from({ length: 3 }, (_, index) => {
      let nextDate = addDays(now, index + startDayOffset);
      let label = `${format(nextDate, 'dd', { locale: ru })} ${format(nextDate, 'MMMM', { locale: ru })}`;

      if (index === 0 && startDayOffset === 0) {
        label += ', до 17:00';
      }

      return {
        value: format(nextDate, 'dd.MM.yyyy', { locale: ru }),
        label
      };
    });
  };

  const getTimeOptions = useCallback((selectedDate: string | null) => {
    const now = new Date();
    const timeOptions = [];

    if (!selectedDate) return [];

    const selectedDateObj = parseSelectedDate(selectedDate);

    if (isNaN(selectedDateObj.getTime())) {
      return [];
    }

    const isToday = selectedDateObj.toDateString() === now.toDateString();

    if (isToday) {
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();

      if (currentHour < 16 || (currentHour === 16 && currentMinutes < 30)) {
        timeOptions.push(
          { value: '16:30', label: '16:30' },
          { value: '17:00', label: '17:00' }
        );
      }
    } else {
      let currentHour = 10;
      let currentMinute = 0;

      while (currentHour < 17 || (currentHour === 17 && currentMinute === 0)) {
        const timeString = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
        timeOptions.push({ value: timeString, label: timeString });

        currentMinute += 30;
        if (currentMinute >= 60) {
          currentMinute = 0;
          currentHour += 1;
        }
      }
    }

    return timeOptions;
  }, []);

  const parseSelectedDate = (dateString: string): Date => {
    try {
      if (dateString.includes('до')) {
        return parse(dateString, 'dd MMMM, до HH:mm', new Date(), { locale: ru });
      } else {
        return parse(dateString, 'dd MMMM', new Date(), { locale: ru });
      }
    } catch (error) {
      return new Date();
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const newHour = new Date().getHours();
      setCurrentHour((prevHour) => (prevHour !== newHour ? newHour : prevHour));
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const dateOptions = useMemo(() => getNextThreeDates(), [currentHour]);
  const timeOptions = useMemo(() => getTimeOptions(selectedDate), [selectedDate, getTimeOptions]);

  const handleDateSelect = (option: { value: string; label: string } | null) => {
    const selectedDate = option?.label ?? null;
    setSelectedDate(selectedDate);
    setSelectedTime(null);
    if (selectedDate) {
      localStorage.setItem('selectedDateLabel', selectedDate);
    }
  };

  const validate = (data: { [key: string]: string }) => {
    const newErrors: { [key: string]: boolean } = {};

    if (!data.name || data.name.trim() === '') {
      newErrors.name = true;
    }

    if (!data.phone || data.phone.trim() === '') {
      newErrors.phone = true;
    }

    if (!selectedDate || selectedDate.trim() === '') {
      newErrors.date = true;
    }

    if (!selectedTime || selectedTime.trim() === '') {
      newErrors.time = true;
    }

    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const data = Object.fromEntries(formData.entries()) as { [key: string]: string };
    const validationErrors = validate(data);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setErrors({});
      sendLeadToWhatsApp({
        title: 'Запись на экскурсию',
        fields: [
          { label: 'Имя', value: data.name },
          { label: 'Телефон', value: data.phone },
          { label: 'Дата', value: selectedDate },
          { label: 'Время', value: selectedTime },
        ],
      });
      setIsSent(true);
    }
  };

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');

    if (digits === '' || digits === '+') {
      return '+7 ';
    }

    let formatted = '+7';

    if (digits.length > 1) {
      formatted += ` ${digits.slice(1, 4)}`;
    }
    if (digits.length > 4) {
      formatted += ` ${digits.slice(4, 7)}`;
    }
    if (digits.length > 7) {
      formatted += `-${digits.slice(7, 9)}`;
    }
    if (digits.length > 9) {
      formatted += `-${digits.slice(9, 11)}`;
    }

    return formatted;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhoneNumber(e.target.value);
    e.target.value = formattedValue;
  };

  const handlePhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.value.startsWith('+7')) {
      e.target.value = '+7';
    }
  };

  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '+7 ' || e.target.value === '+7') {
      e.target.value = '';
    }
  };

  const getInputClassName = (fieldName: string) =>
    cn('text-3-bold_font', styles.form_field, errors[fieldName] && styles.error_field);

  useEffect(() => {
    const scriptExists = document.querySelector(`script[src="${YANDEX_MAP_API_URL}"]`);
    if (!scriptExists) {
      const script = document.createElement("script");
      script.src = YANDEX_MAP_API_URL;
      script.async = true;
      script.onerror = () => {
        console.error('Ошибка загрузки Яндекс.Карт');
      };
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (window.ymaps && mapRef.current) {
      window.ymaps.ready(() => {
        try {
          if (!mapInstance.current) {
            const initialZoom = 2 + (zoomLevel - 2) * (20 - 2) / (200 - 10);
            mapInstance.current = new window.ymaps.Map(mapRef.current, {
              center: centerCoordinates,
              zoom: initialZoom,
              controls: []
            });

            const handleMouseWheel = (e: WheelEvent) => {
              e.preventDefault();
              const delta = e.deltaY > 0 ? -10 : 10;
              setZoomLevel(prevZoom => {
                const newZoom = prevZoom + delta;
                return Math.max(10, Math.min(newZoom, 200));
              });
            };

            if (mapRef.current) {
              mapRef.current.addEventListener('wheel', handleMouseWheel, { passive: false });
            }

            return () => {
              if (mapRef.current) {
                mapRef.current.removeEventListener('wheel', handleMouseWheel);
              }
            };
          }
        } catch (error) {
          console.error('Ошибка инициализации карты:', error);
        }
      });
    } else {
      if (mapInstance.current) {
        mapInstance.current.destroy();
        mapInstance.current = null;
      }
    }
  }, [centerCoordinates, zoomLevel]);

  useEffect(() => {
    if (mapInstance.current) {
      const yandexZoomLevel = 2 + (zoomLevel - 2) * (20 - 2) / (200 - 10);
      mapInstance.current.setZoom(yandexZoomLevel, {
        duration: 300,
        timingFunction: 'ease-in-out'
      });
    }
  }, [zoomLevel]);

  const handleLocationClick = () => {
    if (mapInstance.current) {
      mapInstance.current.panTo(centerCoordinates, {
        duration: 500,
        timingFunction: 'ease-out'
      });
    }
  };

  return (
    <>
      <div className={cn(styles.modal, isActive && styles.active, !isActive && styles.hidden)}>
        <InternalHeader title='Экскурсия' onClose={onClose} />
        <div className={styles.modal_wrapper}>
          {isSent ? (
            <div className={styles.success_page}>
              <div className={styles.success_title}>
                <Icon name="successIcon" width='48' height='48' />
                <h3>Записались</h3>
                <p className='text_body_b1'>
                  Позвоним за день до встречи, встретим вас и все покажем.
                </p>
              </div>
              <div className={styles.excursion_info}>
                <div className={styles.info}>
                  <p className='text_body_b2'>Проект</p>
                  <p className='text_subheaders_sh2'>Статус</p>
                </div>
                <div className={styles.info}>
                  <p className='text_body_b2'>Адрес</p>
                  <p className='text_subheaders_sh2'>Каспийск, ул. Байрамова</p>
                </div>
                <div className={styles.info}>
                  <p className='text_body_b2'>Дата</p>
                  <p className='text_subheaders_sh2'>{selectedDate}</p>
                </div>
                <div className={styles.info}>
                  <p className='text_body_b2'>Время</p>
                  <p className='text_subheaders_sh2'>{selectedTime}</p>
                </div>
              </div>
              <Button onClick={onClose} colorType='blue' roundType='small_rounded'>
                Готово
              </Button>
            </div>
          ) : (
            <>
              <div className={styles.form_content}>
                <div className={styles.title}>
                  <h3>Запишитесь на экскурсию</h3>
                  <p className='text_body_b2'>
                    Укажите нужную информацию, дату и&nbsp;время: свяжемся с&nbsp;вами и&nbsp;напомним о&nbsp;встрече за&nbsp;день до&nbsp;нее
                  </p>
                </div>
                <form className={styles.applicant_form_wrapper} onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor='name'>
                      <input
                        className={cn('text-3-bold_font', styles.form_field, errors.name && styles.error_field)}
                        name='name'
                        id='name'
                        type='text'
                        placeholder='Имя'
                      />
                    </label>
                  </div>
                  <div>
                    <label htmlFor='phone'>
                      <input
                        className={cn('text-3-bold_font', styles.form_field, errors.phone && styles.error_field)}
                        name='phone'
                        id='phone'
                        type='text'
                        placeholder='Телефон'
                        onFocus={handlePhoneFocus}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneBlur}
                      />
                    </label>
                  </div>
                  <div className={styles.dropdown}>
                    <Dropdown 
                      options={dateOptions} 
                      placeholder='Дата'
                      selected={dateOptions.find(option => option.label === selectedDate) || null}
                      onSelect={handleDateSelect}
                      className={cn('text-3-bold_font', styles.form_field, errors.date && styles.error_field)}
                    />
                  </div>
                  {selectedDate && (
                    <div className={styles.dropdown}>
                      <Dropdown 
                        options={timeOptions} 
                        placeholder='Время'
                        selected={timeOptions.find(option => option.value === selectedTime) || null}
                        onSelect={(option) => setSelectedTime(option?.value || null)}
                        className={cn('text-3-bold_font', styles.form_field, errors.time && styles.error_field)}
                      />
                    </div>
                  )}
                  <input type="hidden" name="date" value={selectedDate || ''} />
                  <input type="hidden" name="time" value={selectedTime || ''} />
                  <div className={styles.button}>
                    <Button type='submit' colorType='blue' roundType='small_rounded'>
                      Отправить
                    </Button>
                  </div>
                </form>
              </div>
              <div ref={mapRef} className={styles.map_container}>
                <div className={styles.controls_bar_map}>
                  <div className={styles.location_wrapper} onClick={handleLocationClick}>
                    <Icon name='locationIcon' width='8' height='8' />
                  </div>
                  <ScalePreview zoom={zoomLevel} setZoom={setZoomLevel} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
